<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.WEB.2 — floating softphone, bottom-right, mounted once at
// the app root so it persists across route changes. Header strip ("On call",
// "· recording" only with KAPTION_CALL_RECORDING) + minimise; avatar, name,
// timer, inbox, "Open conversation"; round Mute / Hold / Pass call / Notes
// and a round ruby End; footer with the mic in use and "N waiting". Minimised
// it is a pill with the timer and End.
//
// whatsapp-calling-ownership.UI.3 — "Pass call" opens the picker
// (KaptionCallPassPicker); the dock then shows the pass in progress, the
// consult view (both people, Back to <customer>, Pass to <name>) and the
// "Passed to <name>" confirmation with Undo. The target of a pass sees who
// passed it, the note while being briefed (Leave hands it back), and a muted
// hint after a pass_now.
//
// whatsapp-calling.WEB.9 — a media failure is never silent: "Microphone
// blocked" / "Couldn't connect audio" with Retry (End stays below).
//
// whatsapp-call-transcripts.REC.UI — a recorded call shows a red
// "Recording" chip in the header. The inbox's notice mode decides the rest:
// 'button' gives a "Play recording notice" button (disabled once played,
// then "Notice played 0:03"), 'agent' reminds the agent to say it. The
// connected agent can "Stop recording" (CONSENT.4).
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionCallPassPicker from './KaptionCallPassPicker.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ai.UI.3
import KaptionCallAiChip from './KaptionCallAiChip.vue';
import { CLIENT_STATE } from './KaptionCallClient';
import {
  CALL_STATUS,
  PASS_STATUS,
  formatCallDuration,
  quoteNote,
} from './kaptionCallModel';

const props = defineProps({
  call: { type: Object, required: true },
  heldCall: { type: Object, default: null },
  muted: { type: Boolean, default: false },
  onHold: { type: Boolean, default: false },
  minimised: { type: Boolean, default: false },
  clientState: { type: String, default: CLIENT_STATE.IDLE },
  waitingCount: { type: Number, default: 0 },
  micLabel: { type: String, default: '' },
  recording: { type: Boolean, default: false },
  // whatsapp-call-transcripts.REC.UI
  recordingStopped: { type: Boolean, default: false },
  noticeMode: { type: String, default: 'auto' },
  noticePlayedAt: { type: String, default: null },
  noticeBusy: { type: Boolean, default: false },
  canStopRecording: { type: Boolean, default: false },
  // whatsapp-calling-ownership.PASS.1 — only the connected agent or an admin.
  canPass: { type: Boolean, default: true },
  passTargets: { type: Array, default: () => [] },
  passTargetsLoading: { type: Boolean, default: false },
  passBusy: { type: Boolean, default: false },
  outgoingPass: { type: Object, default: null },
  passedNotice: { type: Object, default: null },
  // The pass that brought this call to me (I am its target), if any.
  receivedPass: { type: Object, default: null },
  mutedAfterPass: { type: Boolean, default: false },
  // whatsapp-calling.WEB.9 — null | 'mic_blocked' | 'audio_failed'.
  mediaError: { type: String, default: null },
});

const emit = defineEmits([
  'toggleMute',
  'toggleHold',
  'openPass',
  'pass',
  'cancelPass',
  'completePass',
  'takeCall',
  'undoPass',
  'leavePass',
  'notes',
  'end',
  'minimise',
  'expand',
  'openConversation',
  'retryMedia',
  'playNotice',
  'stopRecording',
]);

const now = ref(Date.now());
let ticker = null;
onMounted(() => {
  ticker = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});
onBeforeUnmount(() => clearInterval(ticker));

const showPicker = ref(false);

const firstName = name => (name || '').split(' ')[0] || '';
const secondsSince = iso => {
  const at = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(at) ? 0 : Math.max(0, (now.value - at) / 1000);
};

// The picker closes once a pass is under way (or the call ends).
watch(
  () => props.outgoingPass?.id,
  id => {
    if (id) showPicker.value = false;
  }
);

const isConsulting = computed(
  () => props.outgoingPass?.status === PASS_STATUS.CONSULTING
);
const isPassRinging = computed(
  () => props.outgoingPass?.status === PASS_STATUS.RINGING
);
const isBeingBriefed = computed(
  () => props.receivedPass?.status === PASS_STATUS.CONSULTING
);
const passedBy = computed(() =>
  props.receivedPass?.status === PASS_STATUS.COMPLETED
    ? props.receivedPass.fromUser?.name || ''
    : ''
);
// whatsapp-calling-ai.UI.3 — the pass came from the Studio Chat AI.
const passedByAssistant = computed(
  () => props.receivedPass?.fromUser?.kind === 'assistant'
);
const consultSince = computed(() =>
  formatCallDuration(
    secondsSince(
      props.outgoingPass?.acceptedAt || props.outgoingPass?.expiresAt
    )
  )
);
const undoLeft = computed(() => {
  const until = props.passedNotice?.undoUntil
    ? Date.parse(props.passedNotice.undoUntil)
    : NaN;
  return Number.isNaN(until)
    ? 0
    : Math.max(0, Math.ceil((until - now.value) / 1000));
});
const canUndo = computed(
  () => !!props.passedNotice?.canUndo && undoLeft.value > 0
);

const openPicker = () => {
  if (props.outgoingPass) return;
  showPicker.value = !showPicker.value;
  if (showPicker.value) emit('openPass');
};

const submitPass = payload => emit('pass', payload);

const isOutboundRinging = computed(
  () =>
    props.call.direction === 'outbound' &&
    props.call.status === CALL_STATUS.RINGING
);

const elapsed = computed(() => {
  const start = props.call.answeredAt
    ? Date.parse(props.call.answeredAt)
    : null;
  if (!start || Number.isNaN(start)) return '00:00';
  return formatCallDuration((now.value - start) / 1000);
});

// "Notice played 0:03" — when it played, on the call's clock.
const noticePlayedClock = computed(() => {
  const played = props.noticePlayedAt ? Date.parse(props.noticePlayedAt) : NaN;
  const start = props.call.answeredAt ? Date.parse(props.call.answeredAt) : NaN;
  if (Number.isNaN(played)) return '';
  return formatCallDuration(
    Number.isNaN(start) ? 0 : Math.max(0, (played - start) / 1000)
  );
});
const showRecordingRow = computed(
  () =>
    (props.recording || props.recordingStopped) &&
    props.call.status === CALL_STATUS.ANSWERED
);

const MEDIA_ERROR_KEYS = {
  mic_blocked: 'KAPTION_CALLS.DOCK.MIC_BLOCKED',
  audio_failed: 'KAPTION_CALLS.DOCK.AUDIO_FAILED',
};
const mediaErrorKey = computed(() =>
  props.mediaError
    ? MEDIA_ERROR_KEYS[props.mediaError] || MEDIA_ERROR_KEYS.audio_failed
    : null
);

const statusKey = computed(() => {
  if (mediaErrorKey.value) return mediaErrorKey.value;
  if (isOutboundRinging.value) return 'KAPTION_CALLS.DOCK.CALLING';
  if (props.onHold) return 'KAPTION_CALLS.DOCK.ON_HOLD';
  if (props.clientState === CLIENT_STATE.CONNECTING)
    return 'KAPTION_CALLS.DOCK.CONNECTING';
  if (props.clientState === CLIENT_STATE.RECONNECTING)
    return 'KAPTION_CALLS.DOCK.RECONNECTING';
  if (props.clientState === CLIENT_STATE.FAILED)
    return 'KAPTION_CALLS.DOCK.MEDIA_FAILED';
  return 'KAPTION_CALLS.DOCK.ON_CALL';
});

const statusDotClass = computed(() => {
  if (props.mediaError || props.clientState === CLIENT_STATE.FAILED)
    return 'bg-n-ruby-9';
  if (props.onHold || isOutboundRinging.value) return 'bg-n-amber-9';
  if (props.clientState === CLIENT_STATE.CONNECTED) return 'bg-n-teal-9';
  return 'bg-n-amber-9';
});

const controls = computed(() => [
  {
    key: 'mute',
    icon: props.muted ? 'i-lucide-mic-off' : 'i-lucide-mic',
    label: props.muted
      ? 'KAPTION_CALLS.DOCK.UNMUTE'
      : 'KAPTION_CALLS.DOCK.MUTE',
    active: props.muted,
    action: () => emit('toggleMute'),
  },
  {
    key: 'hold',
    icon: props.onHold ? 'i-lucide-play' : 'i-lucide-pause',
    label: props.onHold
      ? 'KAPTION_CALLS.DOCK.RESUME'
      : 'KAPTION_CALLS.DOCK.HOLD',
    active: props.onHold,
    action: () => emit('toggleHold'),
  },
  {
    key: 'pass',
    icon: 'i-lucide-arrow-right-left',
    label: 'KAPTION_CALLS.DOCK.PASS_CALL',
    active: showPicker.value,
    disabled:
      !props.canPass ||
      !!props.outgoingPass ||
      props.call.status !== CALL_STATUS.ANSWERED,
    action: openPicker,
  },
  {
    key: 'notes',
    icon: 'i-lucide-notebook-pen',
    label: 'KAPTION_CALLS.DOCK.NOTES',
    active: false,
    action: () => emit('notes'),
  },
]);
</script>

<template>
  <div
    class="fixed bottom-4 right-4 z-50"
    data-testid="kaption-call-dock"
    :data-minimised="minimised"
  >
    <!-- NOTICE: KAPTION-INTEGRATION - Warm Paper (warm-paper-redesign): a
         floating card with the one overlay shadow, header = eyebrow over a
         hairline, controls are linen tiles, End is the one ruby action.
         Classes only; props/emits/state are unchanged. -->
    <!-- Minimised pill -->
    <div
      v-if="minimised"
      data-testid="kaption-call-dock-pill"
      class="flex items-center gap-2 py-1.5 pl-3 pr-1.5 rounded-xl border border-n-weak bg-n-solid-1 shadow-[0_4px_12px_rgb(0_0_0/0.06),0_16px_32px_-16px_rgb(0_0_0/0.22)]"
    >
      <button
        type="button"
        class="flex items-center gap-2 min-w-0"
        :aria-label="$t('KAPTION_CALLS.DOCK.EXPAND')"
        @click="emit('expand')"
      >
        <span class="rounded-full size-2 shrink-0" :class="statusDotClass" />
        <span class="text-sm font-medium text-n-slate-12 truncate max-w-32">
          {{ call.contactName }}
        </span>
        <span class="font-mono text-xs tabular-nums text-n-slate-11">
          {{ isOutboundRinging ? $t('KAPTION_CALLS.DOCK.CALLING') : elapsed }}
        </span>
        <span v-if="muted" class="i-lucide-mic-off size-3.5 text-n-ruby-11" />
      </button>
      <Button
        data-testid="kaption-dock-pill-end"
        icon="i-lucide-phone-off"
        color="ruby"
        size="sm"
        :aria-label="$t('KAPTION_CALLS.DOCK.END')"
        @click="emit('end')"
      />
    </div>

    <!-- whatsapp-calling-ownership.UI.3 — "Passed to <name>" + Undo -->
    <div
      v-else-if="passedNotice"
      data-testid="kaption-dock-passed"
      class="flex w-80 items-center gap-3 px-4 py-3 rounded-xl border border-n-weak bg-n-solid-1 shadow-[0_4px_12px_rgb(0_0_0/0.06),0_16px_32px_-16px_rgb(0_0_0/0.22)]"
    >
      <span
        class="flex size-8 shrink-0 items-center justify-center rounded-md bg-n-alpha-2 text-n-teal-11"
      >
        <span class="i-lucide-check size-4" />
      </span>
      <span class="flex min-w-0 flex-1 flex-col">
        <span class="truncate text-sm font-medium text-n-slate-12">
          {{
            $t('KAPTION_CALLS.PASS.PASSED_TO', {
              name: passedNotice.toUser?.name || '',
            })
          }}
        </span>
        <span class="truncate text-xs text-n-slate-11">
          {{
            $t('KAPTION_CALLS.PASS.PASSED_SUB', {
              duration: formatCallDuration(passedNotice.onItSeconds),
            })
          }}
        </span>
      </span>
      <Button
        v-if="canUndo"
        data-testid="kaption-dock-undo"
        :label="$t('KAPTION_CALLS.PASS.UNDO', { seconds: undoLeft })"
        color="slate"
        variant="faded"
        size="sm"
        @click="emit('undoPass')"
      />
    </div>

    <!-- whatsapp-calling-ownership.UI.3 / PASS.5 — talk_first consult view -->
    <div
      v-else-if="isConsulting"
      data-testid="kaption-dock-consult"
      class="w-80 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-[0_4px_12px_rgb(0_0_0/0.06),0_16px_32px_-16px_rgb(0_0_0/0.22)]"
    >
      <div
        class="flex items-center gap-2 border-b border-n-weak px-4 py-3 text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
      >
        <span class="size-2 rounded-full bg-n-amber-9" />
        {{
          $t('KAPTION_CALLS.PASS.CONSULT_TITLE', {
            name: firstName(outgoingPass.toUser?.name),
          })
        }}
      </div>
      <div class="flex flex-col gap-3 px-4 py-3">
        <div class="flex items-center gap-2">
          <Avatar
            :name="call.contactName || '?'"
            :src="call.contactAvatar"
            :size="32"
            rounded-full
          />
          <span class="flex min-w-0 flex-1 flex-col">
            <span class="truncate text-sm text-n-slate-12">{{
              call.contactName
            }}</span>
            <span class="text-xs text-n-amber-11">
              {{ $t('KAPTION_CALLS.PASS.ON_HOLD') }}
            </span>
          </span>
          <span class="text-xs tabular-nums text-n-slate-11">{{
            elapsed
          }}</span>
        </div>
        <div class="flex items-center gap-2">
          <Avatar
            :name="outgoingPass.toUser?.name || '?'"
            :src="outgoingPass.toUser?.avatarUrl"
            :size="32"
            rounded-full
          />
          <span class="flex min-w-0 flex-1 flex-col">
            <span class="truncate text-sm text-n-slate-12">{{
              outgoingPass.toUser?.name
            }}</span>
            <span class="text-xs text-n-teal-11">
              {{ $t('KAPTION_CALLS.PASS.TALKING_WITH_YOU') }}
            </span>
          </span>
          <span class="text-xs tabular-nums text-n-slate-11">{{
            consultSince
          }}</span>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-2 px-4 pb-3">
        <Button
          data-testid="kaption-dock-back-to-customer"
          :label="
            $t('KAPTION_CALLS.PASS.BACK_TO', {
              name: firstName(call.contactName),
            })
          "
          color="slate"
          variant="faded"
          size="sm"
          :disabled="passBusy"
          @click="emit('cancelPass')"
        />
        <Button
          data-testid="kaption-dock-complete-pass"
          :label="
            $t('KAPTION_CALLS.PASS.PASS_TO', {
              name: firstName(outgoingPass.toUser?.name),
            })
          "
          size="sm"
          :is-loading="passBusy"
          @click="emit('completePass')"
        />
      </div>
    </div>

    <!-- Expanded softphone -->
    <div
      v-else
      class="w-80 overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-[0_4px_12px_rgb(0_0_0/0.06),0_16px_32px_-16px_rgb(0_0_0/0.22)]"
    >
      <div
        class="flex items-center justify-between py-2 pl-4 pr-2 border-b border-n-weak"
      >
        <div
          class="flex items-center gap-2 text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
        >
          <span class="rounded-full size-1.5" :class="statusDotClass" />
          <span data-testid="kaption-dock-status">{{ $t(statusKey) }}</span>
          <span
            v-if="recording && !recordingStopped"
            data-testid="kaption-dock-recording"
            class="inline-flex items-center gap-1 rounded bg-[#DC2626]/10 px-1.5 py-0.5 normal-case tracking-normal text-[#DC2626]"
          >
            <span class="size-1.5 animate-pulse rounded-full bg-[#DC2626]" />
            {{ $t('KAPTION_CALLS.DOCK.RECORDING_CHIP') }}
          </span>
        </div>
        <Button
          data-testid="kaption-dock-minimise"
          icon="i-lucide-minus"
          variant="ghost"
          color="slate"
          size="xs"
          :aria-label="$t('KAPTION_CALLS.DOCK.MINIMISE')"
          @click="emit('minimise')"
        />
      </div>

      <div class="flex flex-col items-center gap-1 px-4 pt-5 pb-4">
        <Avatar
          :name="call.contactName || '?'"
          :src="call.contactAvatar"
          :size="56"
          rounded-full
        />
        <span
          data-testid="kaption-dock-contact"
          class="mt-2 text-base font-medium tracking-tight text-n-slate-12 truncate max-w-full"
        >
          {{ call.contactName }}
        </span>
        <span
          data-testid="kaption-dock-timer"
          class="font-mono text-sm tabular-nums text-n-slate-11"
        >
          {{ isOutboundRinging ? $t('KAPTION_CALLS.DOCK.CALLING') : elapsed }}
        </span>
        <span v-if="call.inboxName" class="text-xs text-n-slate-11 truncate">
          {{ call.inboxName }}
        </span>
        <span
          v-if="passedBy"
          data-testid="kaption-dock-passed-by"
          class="inline-flex max-w-full items-center gap-1 truncate text-xs text-n-slate-11"
        >
          {{ $t('KAPTION_CALLS.PASS.PASSED_BY', { name: passedBy }) }}
          <KaptionCallAiChip v-if="passedByAssistant" icon-only />
        </span>
        <button
          v-if="call.conversationId"
          type="button"
          data-testid="kaption-dock-open-conversation"
          class="mt-1.5 text-xs font-medium text-n-brand hover:underline"
          @click="emit('openConversation')"
        >
          {{ $t('KAPTION_CALLS.DOCK.OPEN_CONVERSATION') }}
        </button>
        <span
          v-if="heldCall"
          data-testid="kaption-dock-held"
          class="mt-1.5 rounded bg-n-amber-3 px-1.5 py-0.5 text-[11px] font-medium text-n-amber-11"
        >
          {{
            $t('KAPTION_CALLS.DOCK.HELD_CALL', { name: heldCall.contactName })
          }}
        </span>
      </div>

      <!-- whatsapp-call-transcripts.REC.UI — notice + stop recording -->
      <div
        v-if="showRecordingRow"
        data-testid="kaption-dock-recording-row"
        class="mx-4 mb-3 flex flex-col gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
      >
        <div class="flex items-center gap-2">
          <span
            class="i-lucide-mic size-3.5 shrink-0"
            :class="recordingStopped ? 'text-n-slate-10' : 'text-[#DC2626]'"
          />
          <span class="min-w-0 flex-1 truncate text-xs text-n-slate-11">
            <template v-if="recordingStopped">
              {{ $t('KAPTION_CALLS.DOCK.RECORDING_STOPPED') }}
            </template>
            <template v-else-if="noticeMode === 'agent'">
              {{ $t('KAPTION_CALLS.DOCK.SAY_NOTICE') }}
            </template>
            <template v-else-if="noticePlayedAt && noticeMode !== 'button'">
              <span data-testid="kaption-dock-notice-played">
                {{
                  $t('KAPTION_CALLS.DOCK.NOTICE_PLAYED', {
                    time: noticePlayedClock,
                  })
                }}
              </span>
            </template>
            <template v-else>
              {{ $t('KAPTION_CALLS.DOCK.RECORDING_CHIP') }}
            </template>
          </span>
          <Button
            v-if="canStopRecording && !recordingStopped"
            data-testid="kaption-dock-stop-recording"
            :label="$t('KAPTION_CALLS.DOCK.STOP_RECORDING')"
            variant="link"
            color="slate"
            size="xs"
            @click="emit('stopRecording')"
          />
        </div>
        <Button
          v-if="noticeMode === 'button' && !recordingStopped"
          data-testid="kaption-dock-play-notice"
          icon="i-lucide-volume-2"
          :label="
            noticePlayedAt
              ? $t('KAPTION_CALLS.DOCK.NOTICE_PLAYED', {
                  time: noticePlayedClock,
                })
              : $t('KAPTION_CALLS.DOCK.PLAY_NOTICE')
          "
          :disabled="!!noticePlayedAt"
          :is-loading="noticeBusy"
          color="ruby"
          variant="faded"
          size="sm"
          class="w-full"
          @click="emit('playNotice')"
        />
      </div>

      <!-- whatsapp-calling.WEB.9 — no mic / no audio session -->
      <div
        v-if="mediaErrorKey"
        data-testid="kaption-dock-media-error"
        role="alert"
        class="mx-4 mb-2 flex items-center gap-2 rounded-xl border border-n-ruby-6 bg-n-ruby-2 px-3 py-2"
      >
        <span
          class="size-4 shrink-0 text-n-ruby-11"
          :class="
            mediaError === 'mic_blocked'
              ? 'i-lucide-mic-off'
              : 'i-lucide-audio-lines'
          "
        />
        <span class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-xs font-medium text-n-ruby-11">
            {{ $t(mediaErrorKey) }}
          </span>
          <span class="text-[11px] text-n-slate-11">
            {{
              mediaError === 'mic_blocked'
                ? $t('KAPTION_CALLS.DOCK.MIC_BLOCKED_HINT')
                : $t('KAPTION_CALLS.DOCK.AUDIO_FAILED_HINT')
            }}
          </span>
        </span>
        <Button
          data-testid="kaption-dock-retry-media"
          :label="$t('KAPTION_CALLS.DOCK.RETRY')"
          color="ruby"
          variant="faded"
          size="xs"
          @click="emit('retryMedia')"
        />
      </div>

      <!-- whatsapp-calling-ownership.PASS.4 — ringing the teammate -->
      <div
        v-if="isPassRinging"
        data-testid="kaption-dock-pass-ringing"
        class="mx-4 mb-3 flex items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
      >
        <span class="i-lucide-arrow-right-left size-4 text-n-slate-11" />
        <span class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-xs font-medium text-n-slate-12">
            {{
              $t('KAPTION_CALLS.PASS.PASSING_TO', {
                name: outgoingPass.toUser?.name || '',
              })
            }}
          </span>
          <span class="text-[11px] text-n-slate-11">
            {{ $t('KAPTION_CALLS.PASS.RINGING_THEM') }}
          </span>
        </span>
        <Button
          data-testid="kaption-dock-cancel-pass"
          :label="$t('KAPTION_CALLS.PASS.CANCEL')"
          variant="ghost"
          color="slate"
          size="xs"
          @click="emit('cancelPass')"
        />
      </div>

      <!-- whatsapp-calling-ownership.PASS.5 / UI.6 — I'm being briefed -->
      <div
        v-if="isBeingBriefed"
        data-testid="kaption-dock-briefing"
        class="mx-4 mb-3 flex flex-col gap-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
      >
        <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
          <KaptionCallAiChip v-if="passedByAssistant" icon-only />
          {{
            $t('KAPTION_CALLS.PASS.BRIEFING', {
              name: firstName(receivedPass.fromUser?.name),
              customer: firstName(call.contactName),
            })
          }}
        </span>
        <span v-if="receivedPass.note" class="text-xs italic text-n-slate-12">
          {{ quoteNote(receivedPass.note) }}
        </span>
        <!-- whatsapp-calling-ai.UI.3 — take the call from the AI -->
        <Button
          v-if="passedByAssistant"
          data-testid="kaption-dock-take-call"
          :label="$t('KAPTION_CALLS.AI.TAKE_CALL')"
          icon="i-lucide-phone"
          size="sm"
          class="mt-1"
          :is-loading="passBusy"
          @click="emit('takeCall')"
        />
        <Button
          data-testid="kaption-dock-leave-pass"
          :label="$t('KAPTION_CALLS.PASS.LEAVE')"
          variant="link"
          color="slate"
          size="xs"
          class="self-start"
          @click="emit('leavePass')"
        />
      </div>

      <p
        v-if="mutedAfterPass && muted"
        data-testid="kaption-dock-muted-after-pass"
        class="mx-4 mb-3 rounded-md bg-n-amber-3 px-2.5 py-1.5 text-xs text-n-amber-11"
      >
        {{
          $t('KAPTION_CALLS.PASS.MUTED_AFTER_PASS', {
            name: firstName(call.contactName),
          })
        }}
      </p>

      <KaptionCallPassPicker
        v-if="showPicker"
        :targets="passTargets"
        :loading="passTargetsLoading"
        :busy="passBusy"
        :customer-name="call.contactName"
        :elapsed="elapsed"
        @pass="submitPass"
        @close="showPicker = false"
      />

      <div v-if="!showPicker" class="grid grid-cols-4 gap-2 px-4 pb-3">
        <div
          v-for="control in controls"
          :key="control.key"
          class="flex flex-col items-center gap-1"
        >
          <Button
            :data-testid="`kaption-dock-${control.key}`"
            :icon="control.icon"
            :color="control.active ? 'blue' : 'slate'"
            variant="faded"
            size="md"
            class="!size-11"
            :disabled="control.disabled"
            :aria-label="$t(control.label)"
            :aria-pressed="control.active"
            @click="control.action"
          />
          <span class="text-[11px] leading-tight text-n-slate-11">{{
            $t(control.label)
          }}</span>
        </div>
      </div>

      <div class="flex px-4 pb-4">
        <Button
          data-testid="kaption-dock-end"
          icon="i-lucide-phone-off"
          :label="$t('KAPTION_CALLS.DOCK.END')"
          color="ruby"
          size="md"
          class="w-full"
          :aria-label="$t('KAPTION_CALLS.DOCK.END')"
          @click="emit('end')"
        />
      </div>

      <div
        class="flex items-center justify-between gap-2 border-t border-n-weak px-4 py-2.5 text-xs text-n-slate-11"
      >
        <span class="flex items-center gap-1 min-w-0">
          <span class="i-lucide-mic size-3.5 shrink-0" />
          <span data-testid="kaption-dock-mic" class="truncate">
            {{ micLabel || $t('KAPTION_CALLS.DOCK.DEFAULT_MIC') }}
          </span>
        </span>
        <span
          v-if="waitingCount"
          data-testid="kaption-dock-waiting"
          class="shrink-0 text-n-amber-11"
        >
          {{ $t('KAPTION_CALLS.DOCK.WAITING', { count: waitingCount }) }}
        </span>
      </div>
    </div>
  </div>
</template>
